// frontend/src/pages/dashboard/SprintPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { sprintAPI, projectAPI, taskAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { pollingManager } from '../../hooks/RealtimePoll';
import { toast } from 'react-toastify';
import {
  Typography, Box, Paper, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, IconButton, Tooltip, Grid, Card,
  CardContent, LinearProgress, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, useTheme, useMediaQuery,
  Divider, Stack, Avatar, Badge, Skeleton, Fade, Collapse,
  InputAdornment, Autocomplete, Tabs, Tab, CardHeader, CardActions
} from '@mui/material';
import {
  Add as AddIcon, PlayArrow as StartIcon, CheckCircle as CompleteIcon,
  Delete as DeleteIcon, Edit as EditIcon, Timeline as BurndownIcon,
  Search as SearchIcon, FilterList as FilterIcon, Close as CloseIcon,
  Refresh as RefreshIcon, CalendarToday as CalendarIcon,
  Flag as FlagIcon, Speed as SpeedIcon, Assignment as TaskIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon, AccessTime as TimeIcon, ViewKanban as ViewKanbanIcon,
} from '@mui/icons-material';

// Modern color palette matching app design
const COLORS = {
  primary: "#4361ee",
  secondary: "#3f37c9",
  success: "#4cc9f0",
  warning: "#f72585",
  info: "#4895ef",
  light: "#f8f9fa",
  dark: "#212529",
  gray: "#6c757d",
  cardBg: "#ffffff",
  surface: "#f0f4f8",
  highlight: "rgba(67, 97, 238, 0.15)"
};

const SprintPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // States
  const [sprints, setSprints] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('add');
  const [currentSprint, setCurrentSprint] = useState(null);
  const [formData, setFormData] = useState({
    name: '', goal: '', start_date: '', end_date: '', committed_points: '', duration_weeks: 2
  });
  const [saving, setSaving] = useState(false);

  // Search & Filter States
  const [searchOpen, setSearchOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const searchInputRef = useRef(null);

  // Expandable cards
  const [expandedSprint, setExpandedSprint] = useState(null);

  // Optimistic update tracking for animations
  const [updatedSprintIds, setUpdatedSprintIds] = useState(new Set());

  // Action lock to prevent rapid duplicate clicks
  const [actionLock, setActionLock] = useState(null);

  // PERMISSION CHECK
  const canView = user?.hasPermission('sprint.view') || user?.hasPermission('project.view.all');
  const canCreate = user?.hasPermission('sprint.create') || user?.hasPermission('sprint.manage');
  const canEdit = user?.hasPermission('sprint.update') || user?.hasPermission('sprint.manage');
  const canDelete = user?.hasPermission('sprint.delete');
  const canStart = user?.hasPermission('sprint.start') || user?.hasPermission('sprint.manage');
  const canComplete = user?.hasPermission('sprint.complete') || user?.hasPermission('sprint.manage');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [projectRes, sprintsRes] = await Promise.all([
        projectAPI.getById(projectId),
        sprintAPI.getAll(projectId)
      ]);
      setProject(projectRes.data);
      setSprints(sprintsRes.data);
    } catch (err) {
      toast.error('Failed to load sprints');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Calculate end date helper
  const calculateEndDate = (startDate, weeks) => {
    if (!startDate || !weeks) return '';
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + (weeks * 7));
    return end.toISOString().split('T')[0];
  };

  // Focus search when expanded
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (projectId) loadData();
  }, [projectId, loadData]);

  // Filter sprints
  const filteredSprints = useMemo(() => {
    return sprints.filter(sprint => {
      const matchesSearch = !searchQuery ||
        sprint.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sprint.goal?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || sprint.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [sprints, searchQuery, filterStatus]);

  // Real-time polling setup
  useEffect(() => {
    if (projectId) {
      loadData(); // Fetch on mount
    }
  }, [projectId]);


  // Helper: Mark sprint as recently updated for animation
  const markSprintUpdated = useCallback((sprintId) => {
    setUpdatedSprintIds(prev => new Set(prev).add(sprintId));
    setTimeout(() => {
      setUpdatedSprintIds(prev => {
        const next = new Set(prev);
        next.delete(sprintId);
        return next;
      });
    }, 800);
  }, []);

  // Helper: Execute action with lock to prevent race conditions
  const withActionLock = useCallback(async (key, fn) => {
    if (actionLock === key) return;
    setActionLock(key);
    try {
      await fn();
    } finally {
      setActionLock(null);
    }
  }, [actionLock]);

  if (!canView) {
    return (
      <DashboardLayout>
        <Box p={3}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            You do not have permission to view sprints.
          </Alert>
        </Box>
      </DashboardLayout>
    );
  }

  // Reset filters
  const resetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
  };

  // Render filter chips
  const renderFilterChips = () => {
    const chips = [];
    if (searchQuery) chips.push({ label: `Search: "${searchQuery}"`, onRemove: () => setSearchQuery('') });
    if (filterStatus !== 'all') chips.push({ label: `Status: ${filterStatus}`, onRemove: () => setFilterStatus('all') });
    return chips;
  };

  // Dialog handlers
  const handleOpenDialog = (type = 'add', sprint = null) => {
    setDialogType(type);
    if (sprint) {
      setCurrentSprint(sprint);
      setFormData({
        name: sprint.name,
        goal: sprint.goal || '',
        start_date: sprint.start_date,
        end_date: sprint.end_date,
        committed_points: sprint.committed_points,
        duration_weeks: sprint.duration_weeks || 2
      });
    } else {
      setCurrentSprint(null);
      const today = new Date().toISOString().split('T')[0];
      const twoWeeksLater = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      setFormData({
        name: `Sprint ${sprints.length + 1}`,
        goal: '',
        start_date: today,
        end_date: twoWeeksLater,
        committed_points: '',
        duration_weeks: 2
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        end_date: formData.end_date || calculateEndDate(formData.start_date, formData.duration_weeks)
      };

      if (dialogType === 'add') {
        await sprintAPI.create(projectId, payload);
        toast.success('Sprint created successfully');
      } else {
        await sprintAPI.update(projectId, currentSprint.id, payload);
        toast.success('Sprint updated successfully');
      }

      setOpenDialog(false);
      await loadData(); // ✅ Just reload - no polling
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleStart = async (sprintId) => {
    try {
      await sprintAPI.start(projectId, sprintId);
      toast.success('Sprint started');
      await loadData(); // ✅ Just reload
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start sprint');
    }
  };

  const handleComplete = async (sprintId) => {
    try {
      await sprintAPI.complete(projectId, sprintId);
      toast.success('Sprint completed');
      await loadData(); // ✅ Just reload
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete sprint');
    }
  };

  const handleDelete = async (sprintId) => {
    if (!window.confirm('Delete this sprint? This action cannot be undone.')) return;

    try {
      await sprintAPI.delete(projectId, sprintId);
      toast.success('Sprint deleted');
      await loadData(); // ✅ Just reload
    } catch (err) {
      toast.error('Failed to delete sprint');
    }
  };

  const toggleExpand = (sprintId) => {
    setExpandedSprint(expandedSprint === sprintId ? null : sprintId);
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'planning': { bg: '#e0e7ff', text: '#4338ca', border: '#818cf8' },
      'active': { bg: '#dcfce7', text: '#166534', border: '#4ade80' },
      'completed': { bg: '#dbeafe', text: '#1e40af', border: '#60a5fa' },
      'cancelled': { bg: '#fee2e2', text: '#991b1b', border: '#f87171' }
    };
    return colors[status] || colors['planning'];
  };

  // Shared button styles
  const sharedButtonStyles = {
    height: 48,
    px: 3,
    borderRadius: 3,
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'none',
    transition: 'all 0.2s ease-in-out',
    width: isMobile ? "100%" : "auto",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiButton-startIcon': {
      '& > *:nth-of-type(1)': {
        fontSize: isMobile ? 18 : 20,
      },
    },
  };

  // Skeleton Loader Components
  const renderSkeletons = () => (
    <Grid container spacing={3}>
      {[1, 2, 3].map((i) => (
        <Grid item xs={12} md={6} lg={4} key={i}>
          <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="rounded" width={60} height={24} />
              </Box>
              <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={8} sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Skeleton variant="text" width={100} height={16} />
                <Skeleton variant="text" width={80} height={16} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[1, 2, 3, 4].map((btn) => (
                  <Skeleton key={btn} variant="circular" width={40} height={40} />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, background: COLORS.surface, minHeight: '100vh' }}>
          {/* Header Skeleton */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
            <Box>
              <Skeleton variant="text" width={150} height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={200} height={20} />
            </Box>
            <Skeleton variant="rounded" width={160} height={48} />
          </Box>
          {/* Filter Bar Skeleton */}
          <Paper elevation={0} sx={{ width: '100%', p: 2, mb: 3, borderRadius: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Skeleton variant="rounded" width={40} height={40} />
              <Skeleton variant="rounded" width={120} height={40} />
              <Skeleton variant="rounded" width={40} height={40} />
            </Box>
          </Paper>
          {/* Cards Skeleton */}
          {renderSkeletons()}
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Fade in timeout={400}>
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, background: COLORS.surface, minHeight: '100vh', pb: 10 }}>

          {/* Header */}
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3,
            flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0
          }}>
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlagIcon sx={{ color: COLORS.primary }} />
                Sprints
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Project: <strong>{project?.name}</strong> • {filteredSprints.length} sprint{filteredSprints.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
    {/* View Board Button */}
    {user?.hasPermission('project.board.view') && (
      <Button
        variant="outlined"
        startIcon={<ViewKanbanIcon />}
        onClick={() => navigate(`/projects/${projectId}/board`)}
        sx={{
          height: 48,
          px: 3,
          borderRadius: 3,
          fontWeight: 600,
          textTransform: 'none',
          borderColor: 'primary.main',
          color: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.lighter',
            transform: 'translateY(-1px)',
            boxShadow: '0 8px 25px rgba(67,97,238,0.15)',
          },
          transition: 'all 0.2s',
        }}
      >
        View Board
      </Button>
    )}
    
    {/* Create Sprint Button - Existing */}
    {canCreate && (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{
          ...sharedButtonStyles,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
            transform: 'translateY(-1px)',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          },
          '&:active': { transform: 'translateY(0)' },
        }}
      >
        Add Sprint
      </Button>
    )}
  </Box>
          </Box>

          {/* Search & Filter Bar - Modern Design */}
          <Paper
            elevation={0}
            sx={{
              width: '100%', p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 3,
              bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', overflow: 'hidden'
            }}
          >
            <Box sx={{
              display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
              gap: { xs: 1, sm: 1.5 }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, width: '100%'
            }}>
              {/* Expandable Search */}
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { xs: '100%', sm: 'auto' }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                {!searchOpen ? (
                  <IconButton
                    onClick={() => setSearchOpen(true)}
                    size="medium"
                    sx={{
                      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                      width: 40, height: 40, '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText', borderColor: 'primary.main' },
                      transition: 'all 0.2s'
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                ) : (
                  <TextField
                    size="small"
                    inputRef={searchInputRef}
                    placeholder="Search sprints..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>,
                      endAdornment: <InputAdornment position="end">
                        <IconButton size="small" onClick={() => searchQuery ? setSearchQuery('') : setSearchOpen(false)} sx={{ p: 0.5 }}>
                          <CloseIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </IconButton>
                      </InputAdornment>,
                      sx: { borderRadius: 2, bgcolor: 'background.paper', '& fieldset': { borderColor: 'divider' } }
                    }}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 } }}
                  />
                )}
              </Box>

              {/* Desktop Filters */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, alignItems: 'center', flexWrap: 'wrap', ml: { sm: 'auto' } }}>
                <Autocomplete
                  size="small"
                  options={['all', 'planning', 'active', 'completed', 'cancelled']}
                  value={filterStatus === 'all' ? null : filterStatus}
                  onChange={(e, newValue) => setFilterStatus(newValue || 'all')}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Status" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper', height: 40, minWidth: 120 } }} />
                  )}
                  sx={{ minWidth: 120 }}
                />
                <Tooltip title="Clear all filters">
                  <IconButton
                    onClick={resetFilters}
                    size="medium"
                    disabled={!searchQuery && filterStatus === 'all'}
                    sx={{
                      bgcolor: (searchQuery || filterStatus !== 'all') ? 'primary.light' : 'grey.200',
                      color: (searchQuery || filterStatus !== 'all') ? 'primary.contrastText' : 'text.secondary',
                      width: 40, height: 40, '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                      transition: 'all 0.2s', '&.Mui-disabled': { bgcolor: 'grey.200', color: 'text.disabled' }
                    }}
                  >
                    <FilterIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Mobile Filter Toggle */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <Button size="small" variant="outlined" startIcon={<FilterIcon />} onClick={() => setFiltersOpen(!filtersOpen)}
                  sx={{ borderRadius: 2, height: 40, textTransform: 'none', minWidth: 'auto', px: 2 }}>
                  {filtersOpen ? 'Hide' : 'Filters'}
                </Button>
              </Box>
            </Box>

            {/* Mobile Filter Collapse */}
            <Collapse in={filtersOpen} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
                <Autocomplete
                  size="small"
                  options={['all', 'planning', 'active', 'completed', 'cancelled']}
                  value={filterStatus}
                  onChange={(e, newValue) => setFilterStatus(newValue || 'all')}
                  renderInput={(params) => <TextField {...params} label="Status" size="small" />}
                  fullWidth
                />
                <Button size="small" variant="contained" onClick={resetFilters} fullWidth sx={{ borderRadius: 2, height: 40 }}>
                  Clear All Filters
                </Button>
              </Box>
            </Collapse>
          </Paper>

          {/* Filter Chips */}
          {renderFilterChips().length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {renderFilterChips().map((chip, i) => (
                <Chip key={i} label={chip.label} onDelete={chip.onRemove} size="small" onDeleteIcon={<CloseIcon fontSize="small" />} />
              ))}
            </Box>
          )}

          {/* Sprints Grid */}
          {filteredSprints.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
              <FlagIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">No sprints found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {searchQuery || filterStatus !== 'all' ? 'Try adjusting your filters' : 'Create your first sprint to get started!'}
              </Typography>
              
              {canCreate && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ borderRadius: 2 }}>
                  Add Sprint
                </Button>
              )}

            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredSprints.map((sprint) => {
                const statusColors = getStatusColor(sprint.status);
                const isExpanded = expandedSprint === sprint.id;
                const progress = sprint.progress_percentage || 0;
                const isRecentlyUpdated = updatedSprintIds.has(sprint.id);

                return (
                  <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={sprint.id}>
                    <Fade in timeout={300}>
                      <Card sx={{
                        borderRadius: 3,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'}`,
                        border: `2px solid ${statusColors.border}`,
                        animation: isRecentlyUpdated ? 'pulseHighlight 0.6s ease-out' : 'none',
                        '@keyframes pulseHighlight': {
                          '0%': { boxShadow: `0 0 0 0 ${COLORS.highlight}` },
                          '70%': { boxShadow: `0 0 0 10px transparent` },
                          '100%': { boxShadow: `0 0 0 0 transparent` }
                        },
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 25px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.12)'}`
                        }
                      }}>
                        {/* Status Badge */}
                        <Box sx={{
                          position: 'absolute', top: 12, right: 12, px: 1, py: 0.5, borderRadius: 2,
                          bgcolor: statusColors.bg, color: statusColors.text, fontSize: '0.7rem', fontWeight: 600,
                          border: `1px solid ${statusColors.border}`, textTransform: 'capitalize',
                          zIndex: 1
                        }}>
                          {sprint.status}
                        </Box>

                        <CardHeader
                          avatar={
                            <Avatar sx={{ bgcolor: statusColors.border, color: 'white', fontWeight: 600 }}>
                              {sprint.name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                          }
                          title={
                            <Typography fontWeight={700} sx={{ pr: 6 }}>{sprint.name}</Typography>
                          }
                          subheader={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <CalendarIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {sprint.start_date ? new Date(sprint.start_date).toLocaleDateString() : 'No start date'}
                              </Typography>
                            </Box>
                          }
                        />

                        <CardContent sx={{ pt: 0 }}>
                          {/* Goal */}
                          {sprint.goal && (
                            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                              <Typography variant="body2" color="text.primary" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <FlagIcon fontSize="small" sx={{ color: COLORS.info, mt: 0.2 }} />
                                {sprint.goal}
                              </Typography>
                            </Box>
                          )}

                          {/* Progress */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" fontWeight={600}>Progress</Typography>
                              <Typography variant="caption" fontWeight={600}>{progress}%</Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: 8, borderRadius: 2,
                                '& .MuiLinearProgress-bar': {
                                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                              {sprint.velocity || 0}/{sprint.committed_points} points completed
                            </Typography>
                          </Box>

                          {/* Stats Grid */}
                          <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item size={{ xs: 6 }}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.lighter', borderRadius: 2 }}>
                                <SpeedIcon fontSize="small" color="success" />
                                <Typography variant="caption" display="block" fontWeight={700}>{sprint.velocity || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">Velocity</Typography>
                              </Box>
                            </Grid>
                            <Grid item size={{ xs: 6 }}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.lighter', borderRadius: 2 }}>
                                <TimeIcon fontSize="small" color="info" />
                                <Typography variant="caption" display="block" fontWeight={700}>{sprint.days_remaining || 0}d</Typography>
                                <Typography variant="caption" color="text.secondary">Remaining</Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Expanded Section */}
                          <Collapse in={isExpanded}>
                            <Divider sx={{ my: 2 }} />
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  <strong>End:</strong> {sprint.end_date ? new Date(sprint.end_date).toLocaleDateString() : 'Not set'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TaskIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  <strong>Duration:</strong> {sprint.duration_weeks || 2} week{((sprint.duration_weeks || 2) !== 1) ? 's' : ''}
                                </Typography>
                              </Box>
                              {sprint.burndown_data && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <BurndownIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>Burndown:</strong> On track
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Collapse>
                        </CardContent>

                        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                          <Button size="small" onClick={() => toggleExpand(sprint.id)}
                            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ textTransform: 'none', fontWeight: 500 }}>
                            {isExpanded ? 'Less' : 'More'}
                          </Button>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {sprint.status === 'planning' && canStart && (
                              <Tooltip title="Start Sprint">
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => handleStart(sprint.id)}
                                  disabled={actionLock === `start-${sprint.id}`}
                                  sx={{
                                    bgcolor: 'success.lighter',
                                    '&:hover': { bgcolor: 'success.light' },
                                    '&.Mui-disabled': { bgcolor: 'success.lighter', opacity: 0.7 }
                                  }}
                                >
                                  {actionLock === `start-${sprint.id}` ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <StartIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {sprint.status === 'active' && canComplete && (
                              <Tooltip title="Complete Sprint">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleComplete(sprint.id)}
                                  disabled={actionLock === `complete-${sprint.id}`}
                                  sx={{
                                    bgcolor: 'primary.lighter',
                                    '&:hover': { bgcolor: 'primary.light' },
                                    '&.Mui-disabled': { bgcolor: 'primary.lighter', opacity: 0.7 }
                                  }}
                                >
                                  {actionLock === `complete-${sprint.id}` ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <CompleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleOpenDialog('edit', sprint)}
                                  sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(sprint.id)}
                                  disabled={actionLock === `delete-${sprint.id}`}
                                  sx={{
                                    bgcolor: 'error.lighter',
                                    '&:hover': { bgcolor: 'error.light' },
                                    '&.Mui-disabled': { bgcolor: 'error.lighter', opacity: 0.7 }
                                  }}
                                >
                                  {actionLock === `delete-${sprint.id}` ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </CardActions>
                      </Card>
                    </Fade>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Create/Edit Dialog - Modern Design */}
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.15)' } }}>
            <DialogTitle sx={{ pb: 1, fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FlagIcon sx={{ color: COLORS.primary }} />
              {dialogType === 'add' ? 'Create New Sprint' : 'Edit Sprint'}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <TextField
                label="Sprint Name *"
                fullWidth
                margin="normal"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                InputProps={{ sx: { borderRadius: 2 } }}
              />
              <TextField
                label="Sprint Goal"
                fullWidth
                margin="normal"
                multiline
                rows={2}
                value={formData.goal}
                onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                placeholder="What do you want to achieve in this sprint?"
                InputProps={{ sx: { borderRadius: 2 } }}
              />

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Sprint Duration</InputLabel>
                    <Select
                      value={formData.duration_weeks}
                      onChange={(e) => {
                        const weeks = parseInt(e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          duration_weeks: weeks,
                          end_date: calculateEndDate(prev.start_date, weeks)
                        }));
                      }}
                      label="Sprint Duration"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value={1}>1 Week</MenuItem>
                      <MenuItem value={2}>2 Weeks (Recommended)</MenuItem>
                      <MenuItem value={3}>3 Weeks</MenuItem>
                      <MenuItem value={4}>4 Weeks</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Committed Points"
                    type="number"
                    fullWidth
                    margin="normal"
                    value={formData.committed_points}
                    onChange={(e) => setFormData({ ...formData, committed_points: e.target.value })}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Start Date *"
                    type="date"
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    value={formData.start_date}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        start_date: newStartDate,
                        end_date: calculateEndDate(newStartDate, prev.duration_weeks)
                      }));
                    }}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    helperText="Auto-calculated from duration"
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button onClick={() => setOpenDialog(false)} color="inherit" sx={{ textTransform: 'none', fontWeight: 500 }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit} disabled={saving}
                sx={{
                  textTransform: 'none', fontWeight: 600, px: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)` }
                }}>
                {saving ? (
                  <><CircularProgress size={18} sx={{ mr: 1 }} /> {dialogType === 'add' ? 'Creating...' : 'Updating...'}</>
                ) : dialogType === 'add' ? 'Create Sprint' : 'Update Sprint'}
              </Button>
            </DialogActions>
          </Dialog>

        </Box>
      </Fade>
    </DashboardLayout>
  );
};

export default SprintPage;